import type { ReactNode } from 'react';
import { staleClashInputSx } from './staleClash';
import { StaleNoteContent, type StaleNoteOptions } from './StaleNote';
import type { StaleConflict } from './useStaleEdit';

export interface StaleFieldOptions<V> extends StaleNoteOptions<V> {
  /** The field's own helper text (a validation message wins over the note). */
  helperText?: ReactNode;
  error?: boolean;
}

/**
 * The per-field half of board C, as props for an MUI `TextField` (or any field
 * taking `helperText` and `sx`): the note from {@link StaleNoteContent} as the
 * helper text, and the clash ring on the input's own border. A control with no
 * such props goes inside `StaleNote` instead.
 */
export function staleFieldProps<T extends Record<string, unknown>, K extends keyof T & string>(
  conflict: StaleConflict<T> | null,
  field: K,
  options: StaleFieldOptions<T[K]> = {},
): { helperText?: ReactNode; sx?: object } {
  const status = conflict?.status[field];
  if (!conflict || !status || status === 'same' || options.error) {
    return { helperText: options.helperText };
  }
  const { label, format, onUseTheirs } = options;
  return {
    helperText: <StaleNoteContent who={conflict.who} status={status} theirs={conflict.theirs[field]} label={label} format={format} onUseTheirs={onUseTheirs} />,
    sx: status === 'clash' ? staleClashInputSx : undefined,
  };
}
